import React, { FC, useEffect, useState } from 'react';
import ComparePlansModalStyled from './ComparePlansModalStyled';
import { H2 } from '~/components/Typography';
import CloseThin from '~/components/Icons/fill/CloseThin';
import { Modals, Option, Product } from '~/types/products';
import ComparePlansDropdown from './ComparePlansDropdown';
import useWindowSize from '~/hooks/useWindowSize';
import { useSelector } from 'react-redux';
import { ReduxState } from '~/types/redux';
import { useRouter } from 'next/router';
import { trackEvent } from '~/helpers/tracking';
import ComparePlansCardDetail from '~/components/ComparePlansModal/ComparePlansCardDetail';
import ComparePlansAccordion from '~/components/ComparePlansModal/ComparePlansAccordion';
import { get } from 'lodash';
import ComparePlansRow from '~/components/ComparePlansModal/ComparePlansRow';
import { LEGAL_NOTE, PLAN_COMPARISON, PLANS } from '~/constants/plans';
import { Plan } from '~/types/plan';
import ComparePlanButtonSelection from '~/components/ComparePlansModal/ComparePlansButtonSelection';

interface Props {
  visible: boolean;
  setActiveModal: (key?: Modals) => void;
  selectedPlan: Product;
  activeProducts: Product[];
  handleSelectProduct: (product: Product, moveToVas?: boolean) => void;
  moveToVas?: boolean;
}
const plans = PLANS;
const LARGE_PLAN_NAME = 'Internet';
const DEFAULT_PLAN_NAME = '1 Gig';
const planComparisonItems = PLAN_COMPARISON;
const legalNote = LEGAL_NOTE;
const ComparePlansModal: FC<Props> = ({
  visible,
  setActiveModal,
  selectedPlan,
  activeProducts,
  handleSelectProduct,
  moveToVas,
}) => {
  const { xsMobile, mobile } = useWindowSize();
  const { legal } = useSelector((store: ReduxState) => store);
  const { legalMap = {} } = legal || {};
  const router = useRouter();
  const currentPage = router.route.slice(1) || '';

  useEffect(() => {
    if (visible) {
      trackEvent({
        action: 'elementViewed',
        data: {
          elementType: 'Modal',
          location: currentPage,
          position: 'Compare Plans Modal',
          text: 'Compare fiber plans',
        },
      });
    }
  }, [visible]);

  const showFourthColumn = !xsMobile && !mobile;

  const [dropdown1Value, setDropdown1Value] = useState<Option | undefined>();
  const [dropdown2Value, setDropdown2Value] = useState<Option | undefined>();
  const [dropdown3Value, setDropdown3Value] = useState<Option | undefined>();
  const [options, setOptions] = useState<Option[]>([]);
  const [planToCompare1, setPlanToCompare1] = useState<Plan | undefined>();
  const [planToCompare2, setPlanToCompare2] = useState<Plan | undefined>();
  const [planToCompare3, setPlanToCompare3] = useState<Plan | undefined>();
  const handleCancel = () => {
    setActiveModal(undefined);
  };

  const onSelectProduct = (productId: string) => {
    const product = activeProducts.find((p) => p.productId === productId);
    handleSelectProduct(product, moveToVas);
    setActiveModal(undefined);
  };

  useEffect(() => {
    setOptions(
      activeProducts.map((product) => {
        const label = product.name.replace(LARGE_PLAN_NAME, '');
        return {
          label: label,
          value: product.productId,
        };
      })
    );
  }, [activeProducts]);

  useEffect(() => {
    let defaultPlan: Option;
    if (selectedPlan) {
      defaultPlan = {
        label: selectedPlan.name.replace(LARGE_PLAN_NAME, ''),
        value: selectedPlan.productId,
      };
    } else if (options.length > 0) {
      defaultPlan = options.find((option) => option.label.includes(DEFAULT_PLAN_NAME));
    }
    if (defaultPlan) {
      setDropdown1Value(defaultPlan);
      setPlanToCompare1(mapPlanInformation(defaultPlan.value));
    }
  }, [options, selectedPlan]);

  const handleSelect = (optionSelected: Option, dropdownNumber: number) => {
    const plan = mapPlanInformation(optionSelected.value);
    switch (dropdownNumber) {
      case 1:
        setDropdown1Value(optionSelected);
        setPlanToCompare1(plan);
        break;
      case 2:
        setDropdown2Value(optionSelected);
        setPlanToCompare2(plan);
        break;
      case 3:
        setDropdown3Value(optionSelected);
        setPlanToCompare3(plan);
        break;
    }

    trackEvent({
      action: 'elementClicked',
      data: {
        actionOutcome: `Selected ${optionSelected.label}`,
        position: `Compare Plans Dropdown ${dropdownNumber}`,
        elementType: 'Modal',
        location: currentPage,
        text: optionSelected.label,
      },
    });
  };

  const mapPlanInformation = (key: string): Plan => {
    const plan = plans.find((p) => p.productId === key);
    const product = activeProducts.find((p) => p.productId === key);
    plan.price = product?.price?.amount.toString();
    return plan;
  };

  const parseStringToObj = (input: string) => {
    const regex = /^([<>?]?\d+(\.\d+)?)\s(.+)$/;
    const match = input.match(regex);

    if (match) {
      return {
        value: match[1],
        description: match[3],
      };
    }
    return null;
  };

  const mapItems = (key: string) => {
    return [planToCompare1, planToCompare2, planToCompare3].map((plan) => {
      const rowValue = {
        description: '',
        value: '',
        hasFeature: undefined,
      };
      if (!plan) {
        return rowValue;
      }
      const planValue = get(plan.details, key);
      if (typeof planValue === 'boolean') {
        rowValue.hasFeature = planValue;
      } else {
        const parsedValue = parseStringToObj(get(plan.details, key));
        if (parsedValue) {
          rowValue.value = parsedValue.value?.toString();
          rowValue.description = parsedValue.description;
        }
      }
      return rowValue;
    });
  };

  const getUnselectedOptions = (): Option[] => {
    const selectedPlans: Option[] = [planToCompare1, planToCompare2, planToCompare3]
      .filter((plan) => plan)
      .map((plan) => {
        return {
          label: plan?.name,
          value: plan?.productId,
        };
      });
    return options.filter((option) => !selectedPlans.find((plan) => plan.value === option.value));
  };

  return (
    <>
      {visible && (
        <ComparePlansModalStyled visible={visible}>
          <div className="header-container">
            <div className="header">
              {showFourthColumn && <H2 className="title">Compare fiber plans</H2>}
              <CloseThin
                fill="#141928"
                width="20px"
                height="20px"
                onClick={() => handleCancel()}
                data-testid="btn-close"
                className="btn-close"
              />
            </div>
            {!showFourthColumn && <H2 className="title">Compare fiber plans</H2>}
            <p>Choose up to three plans to compare speed, price and other features. </p>
            <div className="choice-container">
              <ComparePlansDropdown
                options={getUnselectedOptions()}
                selectedPlan={dropdown1Value}
                onSelect={(o) => handleSelect(o, 1)}
              />
              <ComparePlansDropdown
                options={getUnselectedOptions()}
                selectedPlan={dropdown2Value}
                onSelect={(o) => handleSelect(o, 2)}
              />
              {showFourthColumn && (
                <ComparePlansDropdown
                  options={getUnselectedOptions()}
                  selectedPlan={dropdown3Value}
                  onSelect={(o) => handleSelect(o, 3)}
                />
              )}
            </div>
          </div>

          <div className="plans-table">
            <div className="details-container">
              <ComparePlansCardDetail
                planDetail={planToCompare1}
                tooltipContent={legalMap[planToCompare1?.legalKey]?.text}
                onSelectProduct={onSelectProduct}
              />
              <ComparePlansCardDetail
                planDetail={planToCompare2}
                tooltipContent={legalMap[planToCompare2?.legalKey]?.text}
                onSelectProduct={onSelectProduct}
              />
              {showFourthColumn && (
                <ComparePlansCardDetail
                  planDetail={planToCompare3}
                  tooltipContent={legalMap[planToCompare3?.legalKey]?.text}
                  onSelectProduct={onSelectProduct}
                />
              )}
            </div>
            {planComparisonItems.map((section, index) => {
              return (
                <ComparePlansAccordion key={`plan_${index}__section`} title={section.title} activeIndex={index}>
                  {section.fields.map((row) => {
                    return (
                      <ComparePlansRow
                        key={`plan_${row.key}__row`}
                        tooltipContent={legalMap[row.legalKey]?.text}
                        items={mapItems(row.key)}
                        title={row.label}
                        showFourthColumn={showFourthColumn}
                      />
                    );
                  })}
                </ComparePlansAccordion>
              );
            })}
            <div className="details-container footer">
              <ComparePlanButtonSelection planDetail={planToCompare1} onSelectProduct={onSelectProduct} />
              <ComparePlanButtonSelection planDetail={planToCompare2} onSelectProduct={onSelectProduct} />
              {showFourthColumn && (
                <ComparePlanButtonSelection planDetail={planToCompare3} onSelectProduct={onSelectProduct} />
              )}
            </div>
            <div className="details-container">
              <span className="footer-note">{legalNote}</span>
            </div>
          </div>
        </ComparePlansModalStyled>
      )}
    </>
  );
};

export default ComparePlansModal;
ComparePlansModal.displayName = 'ComparePlansModal';
